// React Query
import { InfiniteData, QueryClient, QueryKey } from "@tanstack/react-query";

// Types
import { IBasicPaginatedQueryResponse, IGetLastPageFn, IPageParam, IPaginatedResponse } from "@/Common/api/types/pagination";

// Constants
export const PAGE_SIZE = 100;
export const DEFAULT_CURSOR = { after: "", first: PAGE_SIZE } as IPageParam<string>;

export function getAllPaginatedData<T>(data: InfiniteData<IPaginatedResponse<T, unknown>> | undefined) {
	return data?.pages.flatMap((page) => page.edges.map((edge) => edge.node)) ?? [];
}

export function getAllPaginatedItems<T>(data: InfiniteData<IBasicPaginatedQueryResponse<T>> | undefined) {
	return data?.pages.flat().flatMap((page) => page.items);
}

type TGetLastPageFn<T = unknown, TExtraData = unknown> = (defaultPageSize?: number) => IGetLastPageFn<T, TExtraData>;

export const buildGetNextPageParam: TGetLastPageFn =
	(defaultPageSize = PAGE_SIZE) =>
	(lastPage) => {
		if (!lastPage) {
			return DEFAULT_CURSOR;
		}
		if (!lastPage?.pageInfo?.hasNextPage) {
			return undefined;
		}
		return { after: lastPage.pageInfo.endCursor, first: defaultPageSize };
	};

type InfiniteDataWithItems<Page extends { items: unknown[] }> = InfiniteData<Page>;

export function addItemToInfiniteCache<R = unknown, Page extends { items: R[] } = { items: R[] }>(
	queryClient: QueryClient,
	key: QueryKey,
	newItem: Page["items"][number],
	options?: {
		position?: "start" | "end";
		pageIndex?: number;
		sortFn?: (a: Page["items"][number], b: Page["items"][number]) => number;
	}
) {
	queryClient.setQueryData<InfiniteDataWithItems<Page>>(key, (cachedData) => {
		if (!cachedData) {
			return cachedData;
		}

		const { position = "end", pageIndex = 0, sortFn } = options ?? {};

		return {
			...cachedData,
			pages: cachedData.pages.map((page, index) => {
				// Add to a specific page or first page by default
				if (index === pageIndex) {
					let updatedItems: Page["items"];

					if (position === "start") {
						updatedItems = [newItem, ...page.items];
					} else {
						updatedItems = [...page.items, newItem];
					}

					// Apply sorting if provided
					if (sortFn) {
						updatedItems = updatedItems.sort(sortFn);
					}

					return {
						...page,
						items: updatedItems,
					};
				}
				return page;
			}),
		};
	});
}

export function updateItemInInfiniteCache<R = unknown, Page extends { items: R[] } = { items: R[] }>(
	queryClient: QueryClient,
	key: QueryKey,
	predicate: (item: Page["items"][number]) => boolean,
	updater: (item: Page["items"][number]) => Page["items"][number]
) {
	queryClient.setQueryData<InfiniteDataWithItems<Page>>(key, (cachedData) => {
		if (!cachedData) {
			return cachedData;
		}

		return {
			...cachedData,
			pages: cachedData.pages.map((page) => ({
				...page,
				items: page.items.map((item) => (predicate(item) ? updater(item) : item)),
			})),
		};
	});
}

export function deleteItemFromInfiniteCache<R = unknown, Page extends { items: R[] } = { items: R[] }>(
	queryClient: QueryClient,
	key: QueryKey,
	predicate: (item: Page["items"][number]) => boolean
) {
	queryClient.setQueryData<InfiniteDataWithItems<Page>>(key, (cachedData) => {
		if (!cachedData) {
			return cachedData;
		}

		return {
			...cachedData,
			pages: cachedData.pages.map((page) => ({
				...page,
				items: page.items.filter((item) => !predicate(item)),
			})),
		};
	});
}
